import { Badge, Box, Heading } from '@radix-ui/themes';
import { verboseDebug } from 'classes/helpers/debug.helper';
import { ensureInstallationDataCollections } from 'classes/helpers/install-data-collections.helper';
import { MachineContextHelper } from 'classes/helpers/machine-context.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { ReactHelper } from 'classes/helpers/react.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { CommonDialog } from 'components/common/dialogs';
import { MachineConnectionDialog } from 'components/machine/dialogs/connection';
import { MachineControlDialog } from 'components/machine/dialogs/control';
import { HardwareDiagnosticDialog } from 'components/machine/dialogs/hardware-diagnostic';
import { HardwareDiagnosticCancelConfirm } from 'components/machine/dialogs/hardware-diagnostic/cancel-confirm';
import { HealthCheckDialog } from 'components/machine/dialogs/health-check';
import { MachineInspectionDialogHoC } from 'components/machine/dialogs/inspection';
import { InstallationDialogHoC } from 'components/machine/dialogs/installation';
import { InstallationProvider } from 'components/machine/dialogs/installation/context';
import { R2fStatusDialog } from 'components/machine/dialogs/r2f-status';
import { RealignMachineDialog } from 'components/machine/dialogs/realign-machine';
import { VisionCalibrationCameraHoC } from 'components/machine/dialogs/vision-calibration/camera/hoc';
import { VisionCalibrationDistanceHoC } from 'components/machine/dialogs/vision-calibration/distance';
import { IntercomListener } from 'components/main/listeners/intercom';
import { NotificationListener } from 'components/main/listeners/notification';
import env from 'config';
import { HittersContext } from 'contexts/hitters.context';
import { SessionEventsContext } from 'contexts/session-events.context';
import { addDays, isFuture, lightFormat, parseISO } from 'date-fns';
import { CookieKey } from 'enums/cookies.enums';
import { MachineMode } from 'enums/machine.enums';
import useQueueStateListener from 'hooks/machine/useQueueStateListener';
import useLatest from 'hooks/useLatestRef';
import { t } from 'i18next';
import { IVerboseComponent } from 'interfaces/i-components';
import { IFireData } from 'interfaces/i-firing';
import { INotificationButton } from 'interfaces/i-notification';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { MachineHelper } from 'lib_ts/classes/machine.helper';
import {
  FT_TO_INCHES,
  IN_TO_CM,
  METERS_TO_FT,
  MPH_TO_KPH,
} from 'lib_ts/classes/math.utilities';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { getMachineActiveModelID } from 'lib_ts/classes/ms.helper';
import { PlateHelper, VISIBLE_STRIKEZONE } from 'lib_ts/classes/plate.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { VideoHelper } from 'lib_ts/classes/video.helper';
import { DataCollectionType } from 'lib_ts/enums/data-collection.enums';
import { ERROR_MSGS } from 'lib_ts/enums/errors.enums';
import {
  BallStatusMsgType,
  HealthCheckType,
  QueueState,
  SfxName,
  StaticVideoType,
  WsMsgType,
} from 'lib_ts/enums/machine-msg.enum';
import {
  BallType,
  FireOption,
  MS_LIMITS,
  MachineStatus,
  SEAMLESS_BALL_TYPES,
  StrikeZoneType,
} from 'lib_ts/enums/machine.enums';
import {
  BuildPriority,
  PitchType,
  ReferenceListType,
} from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IDataCollection } from 'lib_ts/interfaces/i-data-collection';
import { DEFAULT_CONTEXT_MACHINE, IMachine } from 'lib_ts/interfaces/i-machine';

import { MetricHelper } from 'classes/helpers/metric.helper';
import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { useHardwareDiagnosticStore } from 'components/machine/dialogs/hardware-diagnostic/store/use-hardware-diagnostic-store';
import { MSDebugDialogHoC } from 'components/machine/dialogs/ms-debug';
import { EndTrainingListener } from 'components/machine/listeners/end-training-listener';
import useR2fListener from 'hooks/machine/useR2fListener';
import useRecycleTmsShots from 'hooks/machine/useRecycleTmsShots';
import useSyncReadyToContTrain from 'hooks/machine/useSyncReadyToContTrain';
import useSyncReadyToTrain from 'hooks/machine/useSyncReadyToTrain';
import useSyncTmsDict from 'hooks/machine/useSyncTmsDict';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import useLogout from 'hooks/useLogout';
import usePrevious from 'hooks/usePrevious';
import { DropBallFn } from 'interfaces/function-types';
import { UnitFactors } from 'interfaces/i-machine-context';
import { RestartMode } from 'lib_ts/enums/admin.enums';
import { ServiceCategory } from 'lib_ts/enums/machine-service-logs.enums';
import { CalibrateProc } from 'lib_ts/enums/machine.enums';
import { FF_NAME_ON_DEMAND_MODE } from 'lib_ts/enums/training.enums';
import { DictOf } from 'lib_ts/interfaces/common/i-dictionary';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import {
  DEFAULT_MACHINE_STATE,
  IMachineState,
} from 'lib_ts/interfaces/i-machine-state';
import {
  IFireEvent,
  IMSTargetEventData,
} from 'lib_ts/interfaces/i-session-event';
import { IStrikeZone } from 'lib_ts/interfaces/i-strike-zone';
import { IVideo } from 'lib_ts/interfaces/i-video';
import { ITwoFactorMsg } from 'lib_ts/interfaces/machine-msg/i-2fa';
import { IBallStatusMsg } from 'lib_ts/interfaces/machine-msg/i-ball-status';
import { ICalibrateRequestMsg } from 'lib_ts/interfaces/machine-msg/i-calibrate';
import { IFireMsg } from 'lib_ts/interfaces/machine-msg/i-fire';
import {
  IHealthCheckRequestMsg,
  IHealthCheckResponseMsg,
} from 'lib_ts/interfaces/machine-msg/i-health-check';
import { IMachineResetMsg } from 'lib_ts/interfaces/machine-msg/i-machine-reset';
import {
  IAutoFireMsg,
  IMachineStateMsg,
} from 'lib_ts/interfaces/machine-msg/i-machine-state';
import {
  IProcessQueryResponseMsg,
  IProcessStatus,
} from 'lib_ts/interfaces/machine-msg/i-process-status';
import { IProjectorAdjustmentMsg } from 'lib_ts/interfaces/machine-msg/i-projector-adjustment';
import { IProjectorSfxMsg } from 'lib_ts/interfaces/machine-msg/i-projector-sfx';
import { IPitchPreviewOverlayMsg } from 'lib_ts/interfaces/machine-msg/i-projector-text-overlay';
import { IQueueMsg } from 'lib_ts/interfaces/machine-msg/i-queue-utilities';
import { IRulerMsg } from 'lib_ts/interfaces/machine-msg/i-ruler';
import {
  ISpecialMstargetMsg,
  SpecialMsPosition,
} from 'lib_ts/interfaces/machine-msg/i-special-mstarget';
import { IMachineModel } from 'lib_ts/interfaces/modelling/i-machine-model';
import {
  DEFAULT_BALL_STATE,
  DEFAULT_PLATE,
  DEFAULT_TRAJECTORY,
  IBallDetailsError,
  IBallState,
  IBuildPitchPayload,
  IPitch,
  IPitchList,
  IPlateLoc,
  ITrainingPitch,
} from 'lib_ts/interfaces/pitches';
import { DEFAULT_SEAMS } from 'lib_ts/interfaces/pitches/i-base';
import { IVisionCameraCalibration } from 'lib_ts/interfaces/vision/i-vision-camera-calibration';
import { clone, compact, isEqual, isNil } from 'lodash';
import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { IntercomProvider } from 'react-use-intercom';
import { DataCollectionsService } from 'services/data-collections.service';
import { UserMachineModelsService } from 'services/machine-models.service';
import { MachineServiceLogsService } from 'services/machine-service-logs.service';
import { MachinesService } from 'services/machines.service';
import { PitchesService } from 'services/pitches.service';
import { SessionEventsService } from 'services/session-events.service';
import { createSocket } from 'services/socket';
import { StateTransformService } from 'services/state-transform.service';
import { VisionCameraCalibrationsService as VCCService } from 'services/vision-camera-calibrations';
import { WebSocketService } from 'services/web-socket.service';
import slugify from 'slugify';
import { useAuthStore } from 'stores/use-auth-store';
import { useCookiesStore } from 'stores/use-cookies-store';
import useDropBallStore from 'stores/use-drop-ball-store';
import useMachineStatusStore from 'stores/use-machine-status-store';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';
import { v4 } from 'uuid';

const CONTEXT_NAME = 'MachineContext';

const SUPPRESS_LOW_LIGHT_WARNING = true;

const MIN_WAIT_BETWEEN_FIRE_MS = 2_000;

const DAYS_TO_WARN_HW_CHANGE = 1;

const MOUND_HEIGHT_FT = 10 / 12;

const DEFAULT_LAST_MS: IMachineStateMsg = {
  ...DEFAULT_MACHINE_STATE,
  hash: null,
  force: null,
  jitter: null,
  pitch_info: null,
  video_uuid: StaticVideoType.screensaver,
};

export enum MachineDialogMode {
  Debug = 'Debug',
  Disconnected = 'Disconnected',
  HealthCheck = 'HealthCheck',
  HwDiagnostic = 'HwDiagnostic',
  Inspect = 'Inspect',
  Installation = 'Installation',
  Realign = 'Realign',
  RequestControl = 'RequestControl',
  Restart = 'Restart',
  Status = 'Status',
  VisionCamera = 'VisionCamera',
  VisionDistance = 'VisionDistance',
}

interface IMachineDialogConfig {
  key: number;
  options?: IMachineDialogOptions;
}

type MachineDialogConfigMap = Record<
  MachineDialogMode,
  IMachineDialogConfig | undefined
>;

const DEFAULT_DIALOG_MAP = Object.values(MachineDialogMode).reduce(
  (acc, value) => {
    acc[value as MachineDialogMode] = undefined;
    return acc;
  },
  {} as MachineDialogConfigMap
);

/** if the machine is using any ball type from this array, tracking device validation will always be skipped */
const NO_RAPSODO_BALL_TYPES: BallType[] = [
  BallType.Smash,
  BallType.StingFreeJugs,
];

/** how long before the recently fired pitch is cleared */
const RECENTLY_FIRED_DURATION = 3_000;

interface IMachineDialogOptions {
  hwd?: IDataCollection;
}

type SendPitchPreviewFn = (config: {
  trigger: string;
  prev?: IPitch;
  current: IPitch;
  next?: IPitch;
}) => void;

type SendTargetFn = (config: {
  source: string;
  msMsg: IMachineStateMsg;
  pitch: Partial<IPitch>;
  ref_py: number;
  plate?: IPlateLoc;
  list?: IPitchList;
  hitter_id?: string;
  video?: IVideo;
  // for data collector
  collectionID?: string;
  // for data collector
  aiming?: boolean;
  // skips checks and validations
  force?: boolean;
  trigger?: string;
  // e.g. auto-changing pitches while firing from a queue
  skipFiringCheck?: boolean;
}) => Promise<{ success: boolean; hash?: string }>;

interface LogPlateConfig {
  prevPlateFt: Nullable<number>;
  nextPlateFt: number;
  overrideMachineID?: string; // provide if not inserting the log for the active machine
}

export interface IMachineContext {
  lastFired: number | undefined;
  lastFireID: string | undefined;

  rapidMode: boolean;
  readonly setRapidMode: (v: boolean) => void;

  checkConfidence: boolean;

  lastPitchID: string | undefined;

  lastMS: IMachineStateMsg | undefined;

  lastMSHash: string | undefined;
  readonly resetMSHash: () => void;

  // was the last thing sent something that could (eventually) become R2F (e.g. not a screensaver)
  attemptingR2F: boolean;

  lastBallCount: number | undefined;

  /** overlay ids as of last request */
  lastOverlayIDs?: string[];

  machine: IMachine;

  // for the UI to convert from saved imperial >> display values
  unitFactors: UnitFactors;

  // based on selected hitter (if any) and machine's strikeZoneType value
  activeStrikeZone: IStrikeZone;

  activeModel: IMachineModel | undefined;

  // for all models used by ball types on this machine
  machineModels: IMachineModel[];

  loading: boolean;

  autoFire: boolean;

  // returns the new hash based on last MS + new auto-fire value
  readonly setAutoFire: (value: boolean) => void;

  fireOptions: Set<FireOption>;
  readonly setFireOption: (flag: FireOption, enable: boolean) => void;

  /** true when calibration starts, false after completion */
  calibrating: boolean;

  process_statuses: IProcessStatus[];
  readonly setProcessData: (proc: IProcessQueryResponseMsg) => void;

  // true if the current user is connected + machine is not busy
  isActive: boolean;

  // returns isActive but shows a warning if false
  readonly checkActive: () => boolean;

  readonly sendPitchPreview: SendPitchPreviewFn;

  /** result indicates whether a msg was sent or not */
  readonly sendTarget: SendTargetFn;
  readonly resendLastTarget: (source: string) => void;

  /** bypasses most safety validations */
  readonly sendRawTarget: (
    msg: IMachineStateMsg,
    source: string,
    silently: boolean
  ) => Promise<boolean>;

  /** respond to waiting user, whether their request for control is accepted/rejected */
  readonly sendControlResponse: (msg: IQueueMsg) => void;

  readonly update: (
    machine: Partial<IMachine>,
    silently?: boolean
  ) => Promise<boolean>;

  readonly dropball: DropBallFn;

  // returns flag indicating whether anything was sent
  readonly specialMsTarget: (position: SpecialMsPosition) => boolean;

  readonly calibrate: (procs: CalibrateProc[], source: string) => void;

  readonly send2FA: (source: string, msg: ITwoFactorMsg) => void;

  /** ask the machine which overlays are currently active on projector */
  readonly requestOverlayIDs: (source: string) => void;

  /** called by listener, record whatever was caught */
  readonly setOverlayIDs: (values: string[]) => void;

  readonly sendProcessReset: (source: string, proc: string) => void;

  readonly sendProcessQuery: (source: string) => void;

  readonly sendProcessKill: (source: string, proc: string) => void;

  // restarts the whole operating system + firmware
  readonly restartOS: (source: string) => void;

  // only restarst the firmware
  readonly restartFW: (source: string) => void;

  readonly fire: (config: {
    pitch: Partial<IPitch>;
    fireData: IFireData;
  }) => Promise<{
    event?: IFireEvent;
    success: boolean;
  }>;

  readonly toggleRuler: (source: string, msg: IRulerMsg) => void;

  readonly adjustKeystone: (
    source: string,
    msg: IProjectorAdjustmentMsg
  ) => void;

  /** temporarily stores the pitch that was recently fired, self-clears after a delay
   * use-case: pitch list can highlight the pitch (if its _id matches) until it's cleared
   */
  recentlyFiredPitch?: Partial<IPitch>;

  dialogs: MachineDialogConfigMap;

  readonly openDialog: (
    value: MachineDialogMode,
    options?: IMachineDialogOptions
  ) => void;
  readonly closeDialog: (value: MachineDialogMode) => void;

  // for sidebar to reload and find (any) HWD for reminder
  lastHwdCancelled?: number;
  readonly onCancelHwd: () => void;

  readonly specialMode: MachineMode | undefined;
  readonly setSpecialMode: (mode?: MachineMode) => void;

  readonly onEndTraining: () => void;

  readonly playSound: (effect: SfxName) => void;

  // ensures that different instances don't accidentally mutate each other
  readonly getDefaultPitch: (priority?: BuildPriority) => IPitch;

  healthChecking: boolean;
  healthCheckResults: IHealthCheckResponseMsg | undefined;
  readonly startHealthCheck: (type: HealthCheckType) => void;

  // ! deprecate this since socket.io-client should re-attempt automatically + forever
  readonly reconnect: () => void;

  // should be at most one of each type
  lastDcComplete: IDataCollection[];
  readonly reloadLastDcComplete: () => void;

  // * include the machineID + ballType to create a DataCollection for
  readonly createDataCollection: (
    dcType: DataCollectionType,
    dcMachineID: string,
    dcBallType: BallType
  ) => Promise<IDataCollection | undefined>;

  // if necessary, convert a standalone RT/BTC install into a full install (e.g. when resuming a RT/BTC standalone install from AP > machines)
  readonly extendStandaloneInstallation: () => Promise<void>;

  readonly fetchMachine: () => Promise<void>;

  readonly logPlateDistanceChange: (config: LogPlateConfig) => void;

  readonly getTrainingPitches: (
    pitches: IPitch[],
    options?: {
      forceRebuild?: boolean; // e.g. for view pitch data > inspector
    }
  ) => Promise<ITrainingPitch[]>;
}

const DEFAULT: IMachineContext = {
  lastFired: undefined,
  lastFireID: undefined,

  rapidMode: false,
  setRapidMode: () => console.error(`${CONTEXT_NAME}: not init`),

  checkConfidence: false,

  machine: DEFAULT_CONTEXT_MACHINE,

  unitFactors: {
    fromSpeedMph: 1,
    fromBreaksIn: 1,
    fromPosFt: 1,
    fromDistanceFt: 1,
  },

  activeStrikeZone: VISIBLE_STRIKEZONE,
  activeModel: undefined,

  machineModels: [],

  loading: false,

  lastMS: undefined,
  lastMSHash: undefined,
  resetMSHash: () => console.error(`${CONTEXT_NAME}: not init`),

  attemptingR2F: false,

  lastPitchID: undefined,

  fireOptions: new Set(),
  setFireOption: () => console.error(`${CONTEXT_NAME}: not init`),

  calibrating: false,

  autoFire: false,
  setAutoFire: () => console.error(`${CONTEXT_NAME}: not init`),

  process_statuses: [],
  setProcessData: () => console.error(`${CONTEXT_NAME}: not init`),

  isActive: false,
  checkActive: () => false,

  sendPitchPreview: () => console.error(`${CONTEXT_NAME}: not init`),
  sendTarget: () => new Promise(() => ({ success: false })),
  resendLastTarget: () => console.error(`${CONTEXT_NAME}: not init`),

  sendRawTarget: () => new Promise(() => false),
  update: () => new Promise(() => false),

  lastBallCount: 1,
  dropball: () => false,

  specialMsTarget: () => false,
  calibrate: () => console.error(`${CONTEXT_NAME}: not init`),
  send2FA: () => console.error(`${CONTEXT_NAME}: not init`),
  requestOverlayIDs: () => console.error(`${CONTEXT_NAME}: not init`),
  setOverlayIDs: () => console.error(`${CONTEXT_NAME}: not init`),
  sendProcessReset: () => console.error(`${CONTEXT_NAME}: not init`),
  sendProcessQuery: () => console.error(`${CONTEXT_NAME}: not init`),
  sendProcessKill: () => console.error(`${CONTEXT_NAME}: not init`),
  restartOS: () => console.error(`${CONTEXT_NAME}: not init`),
  restartFW: () => console.error(`${CONTEXT_NAME}: not init`),
  fire: () => new Promise(() => false),

  sendControlResponse: () => console.error(`${CONTEXT_NAME}: not init`),

  toggleRuler: () => console.error(`${CONTEXT_NAME}: not init`),

  adjustKeystone: () => console.error(`${CONTEXT_NAME}: not init`),

  dialogs: DEFAULT_DIALOG_MAP,
  openDialog: () => console.error(`${CONTEXT_NAME}: not init`),
  closeDialog: () => console.error(`${CONTEXT_NAME}: not init`),

  onCancelHwd: () => console.error(`${CONTEXT_NAME}: not init`),

  specialMode: undefined,
  setSpecialMode: () => console.error(`${CONTEXT_NAME}: not init`),

  onEndTraining: () => console.error(`${CONTEXT_NAME}: not init`),
  playSound: () => console.error(`${CONTEXT_NAME}: not init`),

  getDefaultPitch: (priority) => {
    const defaultBs = clone(DEFAULT_BALL_STATE);

    const output: IPitch = {
      _id: '',
      _created: '',
      _changed: '',
      _parent_id: '',
      _parent_def: 'pitch-lists',
      _parent_field: 'pitches',
      bs: defaultBs,
      bs_hash: MachineHelper.getBsHash(defaultBs, priority),
      traj: clone(DEFAULT_TRAJECTORY),
      seams: BallHelper.getSeamsFromBS(defaultBs),
      priority: priority || BuildPriority.Default,
      windupVideoID: null,
      stretchVideoID: null,
    };

    return output;
  },

  healthChecking: false,
  healthCheckResults: undefined,
  startHealthCheck: () => console.error(`${CONTEXT_NAME}: not init`),

  reconnect: () => console.error(`${CONTEXT_NAME}: not init`),

  lastDcComplete: [],
  reloadLastDcComplete: () => console.error(`${CONTEXT_NAME}: not init`),

  createDataCollection: () =>
    new Promise(() => console.error(`${CONTEXT_NAME}: not init`)),

  extendStandaloneInstallation: () =>
    new Promise(() => console.error(`${CONTEXT_NAME}: not init`)),

  fetchMachine: () =>
    new Promise(() => console.error(`${CONTEXT_NAME}: not init`)),

  logPlateDistanceChange: () => console.error(`${CONTEXT_NAME}: not init`),

  getTrainingPitches: () => new Promise(() => []),
};

// todo: refactor this into a store
export const MachineContext = createContext(DEFAULT);

interface IProps extends IVerboseComponent {
  children: ReactNode;
}

export const MachineProvider: FC<IProps> = (props) => {
  const onDemand = useFeatureFlag(FF_NAME_ON_DEMAND_MODE);
  // ! deprecate this if we never use heartbeat msgs
  // useHeartbeatListener();
  useQueueStateListener();
  useR2fListener();

  const calibrated = useMachineStatusStore((s) => s.calibrated);
  const canCalibrate = useMachineStatusStore((s) => s.canCalibrate);
  const eStopPressed = useMachineStatusStore((s) => s.eStopPressed);
  const awaitFiringUntil = useMachineStatusStore(
    (s) => s.fireStateDates.awaitFiringUntil
  );
  const noBall = useMachineStatusStore((s) => s.noBall);
  const queueState = useMachineStatusStore((s) => s.queueState);
  const r2fReady = useMachineStatusStore((s) => s.r2fReady);
  const healthCheckStatus = useMachineStatusStore(
    (s) => s.r2fData?.machine_status
  );

  const current = useAuthStore((s) => s.current);
  const isInGame = useAuthStore((s) => s.isInGame);
  const wsConnected = useAuthStore((s) => s.wsConnected);

  const { logout } = useLogout();

  const app = useCookiesStore((s) => s.app);

  const {
    active: activeHitter,
    hitters,
    increasePitchCount,
  } = useContext(HittersContext);

  const { increaseFired } = useContext(SessionEventsContext);

  const [ballStatusToast, setBallStatusToast] = useState(false);

  const [ballDate, setBallDate] = useState(new Date());
  const [ballCount, setBallCount] = useState(DEFAULT.lastBallCount);

  const [hardwareWarned, setHardwareWarned] = useState(false);
  const [machine, setMachine] = useState(DEFAULT.machine);

  // so we can compare and only refetch models when machine.model_ids actually changes, not just on every machine update
  const prevMachineModelIDs = usePrevious(machine.model_ids);

  const activeStrikeZone = useMemo<IStrikeZone>(() => {
    switch (machine.strikeZoneType) {
      case StrikeZoneType.MlbAbs: {
        return PlateHelper.getMlbAbsStrikeZone(activeHitter?.height_ft);
      }

      case StrikeZoneType.Stringer: {
        return PlateHelper.getStringerStrikeZone(activeHitter?.height_ft);
      }

      default: {
        return VISIBLE_STRIKEZONE;
      }
    }
  }, [activeHitter?.height_ft, machine.strikeZoneType]);

  const [activeModel, setActiveModel] = useState(DEFAULT.activeModel);

  const [lastDcLoaded, setLastDcLoaded] = useState(Date.now());
  const [lastDcComplete, setLastDcComplete] = useState(DEFAULT.lastDcComplete);

  useEffect(() => {
    DataCollectionsService.findLast({
      machineID: machine.machineID,
      ball_type: machine.ball_type,
      complete: true,
    }).then((results) => setLastDcComplete(results));
  }, [lastDcLoaded, machine.machineID, machine.ball_type]);

  const [models, setModels] = useState<IMachineModel[]>(DEFAULT.machineModels);
  const [loading, setLoading] = useState(DEFAULT.loading);
  const [fireOptions, setFireOptions] = useState(DEFAULT.fireOptions);

  // when machine changes (e.g. after load or updates), update fire options
  useEffect(() => {
    setFireOptions((prev) => {
      const nextValue = clone(prev);

      if (machine.enable_confidence_checks) {
        // stop skipping
        console.debug('Confidence Checks: ON');
        nextValue.delete(FireOption.SkipConfidenceCheck);
      } else {
        // start skipping
        console.debug('Confidence Checks: OFF');
        nextValue.add(FireOption.SkipConfidenceCheck);
      }

      return nextValue;
    });
  }, [machine.enable_confidence_checks]);

  // considers other factors to automatically add necessary options (e.g. for specific ball types)
  const safeFireOptions = useMemo(() => {
    const output = clone(fireOptions);

    // certain ball types always skip confidence checks
    if (NO_RAPSODO_BALL_TYPES.includes(machine.ball_type)) {
      output.add(FireOption.SkipConfidenceCheck);
    }

    return Array.from(output);
  }, [machine.ball_type, fireOptions]);

  const [calibrating, setCalibrating] = useState(DEFAULT.calibrating);

  // whenever calibrated flag changes, it means calibration has finished
  useEffect(() => {
    setCalibrating(false);
  }, [calibrated]);

  const lowerMachineRef = useRef<(() => void) | null>(null);

  const isFiring = isFuture(awaitFiringUntil);

  // keep a reference to the latest firing state for use in potential callbacks/closures
  // (e.g. _sendSpecialMsTarget called from a toast)
  const latestIsFiring = useLatest(isFiring);

  useEffect(() => {
    if (!wsConnected) {
      useMachineStatusStore.setState({ queueState: QueueState.UserDC });
    }
  }, [wsConnected]);

  useEffect(() => {
    if (!wsConnected) {
      console.debug(
        `${CONTEXT_NAME} stopped listening for ${WsMsgType.M2U_AutoCalibrateStarted} message (ws is not connected)`
      );
      return;
    }

    const callback = () => {
      NotifyHelper.debug({
        message_md: `Auto-calibrate started @ ${new Date().toISOString()}`,
      });

      console.debug(
        `${CONTEXT_NAME} received ${WsMsgType.M2U_AutoCalibrateStarted} message`
      );

      setCalibrating(true);
    };

    WebSocketHelper.on(WsMsgType.M2U_AutoCalibrateStarted, callback);

    console.debug(
      `${CONTEXT_NAME} started listening for ${WsMsgType.M2U_AutoCalibrateStarted} message (ws is not connected)`
    );

    return () => {
      WebSocketHelper.remove(WsMsgType.M2U_AutoCalibrateStarted, callback);
    };
  }, [wsConnected]);

  const [autoFire, setAutoFire] = useState(DEFAULT.autoFire);
  const [processStatuses, setProcessStatuses] = useState(
    DEFAULT.process_statuses
  );

  /** updated whenever a ms is sent to the machine, used to track what was last sent
   * e.g. to start screensaver without moving anything else */
  const [lastMS, setLastMS] = useState<IMachineStateMsg>(DEFAULT_LAST_MS);

  const [lastPitchID, setLastPitchID] = useState(DEFAULT.lastPitchID);
  const [lastFired, setLastFired] = useState<number>();
  const [lastFireID, setLastFireID] = useState<string>();

  /** hash of lastMS used for quickly detecting differences */
  const [lastMSHash, setLastMSHash] = useState<string>();
  const [lastOverlayIDs, setLastOverlayIDs] = useState(DEFAULT.lastOverlayIDs);

  const [recentFiredPitch, setRecentFiredPitch] = useState(
    DEFAULT.recentlyFiredPitch
  );

  const [dialogs, setDialogs] =
    useState<MachineDialogConfigMap>(DEFAULT_DIALOG_MAP);

  const [lastHwdCancelled, setLastHwdCancelled] = useState<number>();

  useEffect(() => {
    useHardwareDiagnosticStore.getState().loadInProgressHwd(machine.machineID);
  }, [machine.machineID, lastHwdCancelled]);

  const [specialMode, setSpecialMode] = useState<MachineMode>();

  const safeSetMachine = useCallback(
    async (nextMachine: IMachine) => {
      if (
        !hardwareWarned &&
        nextMachine.last_hardware_changed &&
        isFuture(
          addDays(
            parseISO(nextMachine.last_hardware_changed),
            DAYS_TO_WARN_HW_CHANGE
          )
        )
      ) {
        // hardware change happened within the last day and warning has not been shown yet
        NotifyHelper.info({
          message_md: `There was a hardware change for \`${
            nextMachine.machineID
          }\` on ${lightFormat(
            parseISO(nextMachine.last_hardware_changed),
            'yyyy-MM-dd'
          )}. Shot data collected before this change will no longer be usable on this machine.`,
        });

        setHardwareWarned(true);
      }

      setMachine(nextMachine);
    },
    [hardwareWarned]
  );

  // not dependent on MachineContext.machine
  const createDataCollection = useCallback(
    async (
      dcType: DataCollectionType,
      dcMachineID: string,
      dcBallType: BallType
    ) => {
      try {
        const refListType = (() => {
          switch (dcType) {
            case DataCollectionType.BallTypeCalibration: {
              // * BTC => BTC
              return ReferenceListType.BallTypeCalibration;
            }

            case DataCollectionType.HardwareDiagnostic:
            case DataCollectionType.RepeatabilityTest:
            default: {
              // * HWD and RT => RT
              return ReferenceListType.Repeatability;
            }
          }
        })();

        const { list, pitches } =
          await PitchesService.getInstance().getReferenceListWithPitches(
            dcBallType,
            refListType
          );

        if (!list) {
          throw new Error(
            `The ${dcBallType} ball type has not been configured for ${dcType} data collection.`
          );
        }

        console.assert(
          pitches.findIndex((p) => !p._mx_ms) === -1,
          'One or more training pitches for data collection do not have a _mx_ms value defined'
        );

        const output = await DataCollectionsService.createOne({
          user: current.email,
          machineID: dcMachineID,
          type: dcType,
          ball_type: dcBallType,
          collectionID: StringHelper.getCollectionID(),
          listID: list._id,
          complete: false,
          pitches: pitches,
        });

        if (!output) {
          throw new Error(
            `Failed to create a data collection for ${dcType} data collection.`
          );
        }

        return output;
      } catch (e) {
        console.error('createDataCollection', e);

        NotifyHelper.error({
          message_md:
            e instanceof Error
              ? e.message
              : `There was a problem creating a data collection. ${ERROR_MSGS.CONTACT_SUPPORT}`,
        });
      }
    },
    [current.email]
  );

  // reload active model when machine changes (e.g. ball type)
  useEffect(() => {
    const modelID = getMachineActiveModelID(machine);

    if (!modelID) {
      // failed to find active model
      return;
    }

    if (activeModel?._id === modelID) {
      // no change to active model
      return;
    }

    UserMachineModelsService.getInstance()
      .getModel(modelID)
      .then((result) => setActiveModel(result));
  }, [machine]);

  // keep build priority in sync with what the active model supports
  useEffect(() => {
    if (!activeModel) {
      return;
    }

    switch (app.build_priority) {
      case BuildPriority.Breaks: {
        if (!activeModel.supports_breaks) {
          useCookiesStore.getState().setCookie(CookieKey.app, {
            build_priority: BuildPriority.Default,
          });
        }
        return;
      }

      case BuildPriority.Spins: {
        if (!activeModel.supports_spins) {
          useCookiesStore.getState().setCookie(CookieKey.app, {
            build_priority: BuildPriority.Default,
          });
        }
        return;
      }

      default: {
        return;
      }
    }
  }, [activeModel]);

  // this needs to be this way so that other actions which specifically require update to finish successfully can properly await
  const update = useCallback(
    async (
      payload: Partial<IMachine>,
      silently?: boolean
    ): Promise<boolean> => {
      try {
        if (!machine._id) {
          NotifyHelper.warning({
            message_md: `Machine cannot be updated without an ID`,
          });
          return false;
        }

        if (
          payload.releaseFt !== undefined &&
          (payload.releaseFt > MS_LIMITS.RELEASE_FT.MAX ||
            payload.releaseFt < MS_LIMITS.RELEASE_FT.MIN)
        ) {
          if (!silently) {
            NotifyHelper.warning({
              message_md: `Plate distance should be between ${MS_LIMITS.RELEASE_FT.MIN}-${MS_LIMITS.RELEASE_FT.MAX} ft.`,
            });
          }
          return false;
        }

        setLoading(true);

        const ballTypeChanged =
          payload.ball_type && payload.ball_type !== machine.ball_type;

        if (ballTypeChanged) {
          payload.last_ball_type_changed = new Date().toISOString();
        }

        // always use machine's _id so we can't update the wrong thing or fail to update because of missing _id
        const result = await MachinesService.getInstance().update(
          machine._id,
          payload
        );

        if (!result) {
          throw new Error(
            `Empty result received from server while updating machine`
          );
        }

        /** if ball type was changed, show a warning after a brief delay */
        if (ballTypeChanged && !silently) {
          NotifyHelper.ballTypeWarning(result.ball_type);
        }

        safeSetMachine(result);

        return true;
      } catch (e) {
        console.error(e);

        if (!silently) {
          NotifyHelper.error({
            message_md:
              'There was a server error while updating your machine. See console for details.',
          });
        }

        return false;
      } finally {
        setLoading(false);
      }
    },
    [machine, safeSetMachine]
  );

  const isActive = queueState === QueueState.Active;

  const checkActive = useCallback((): boolean => {
    switch (queueState) {
      case QueueState.Busy: {
        NotifyHelper.warning({
          message_md: `You must be the active user on ${machine.machineID} to perform this action.`,
          buttons: [
            {
              label: 'Request Control',
              onClick: () => openDialog(MachineDialogMode.RequestControl),
              dismissAfterClick: true,
            },
          ],
        });
        break;
      }

      case QueueState.UserDC: {
        NotifyHelper.warning({
          message_md: `You must be connected to the server to perform this action.`,
          buttons: [
            {
              label: 'Reconnect',
              onClick: reconnect,
              dismissAfterClick: true,
            },
          ],
        });
        break;
      }

      case QueueState.MachineDC: {
        NotifyHelper.warning({
          message_md: `You must be connected to ${machine.machineID} to perform this action.`,
          buttons: [
            {
              label: 'Reconnect',
              onClick: reconnect,
              dismissAfterClick: true,
            },
          ],
        });
        break;
      }

      default: {
        break;
      }
    }

    return isActive;
  }, [isActive, queueState, machine.machineID]);

  const latestCheckActive = useLatest(checkActive);

  /** helper that also keeps lastMS in sync */
  const sendMsTarget = useCallback(
    (msg: IMachineStateMsg, source: string) => {
      WebSocketService.send(WsMsgType.U2S_MsTarget, msg, source);

      setLastMS(msg);
      setLastMSHash(MiscHelper.hashify(msg));

      console.log(
        `${props.identifier}: S2M ${msg.matching_hash} from ${source} w/ tilt ${msg.tilt} and yaw ${msg.yaw}`
      );

      NotifyHelper.debug({
        message_md: `S2M from ${source}!`,
        buttons: [
          {
            label: 'Save MS',
            dismissAfterClick: true,
            onClick: () => {
              const safeSource = slugify(source, {
                strict: true,
                lower: true,
              });

              const filename = [
                machine.machineID,
                safeSource,
                MachineHelper.getMSHash('matching', msg),
              ].join('-');

              MiscHelper.saveAs(
                new Blob([JSON.stringify(msg, null, 2)]),
                `${filename}.json`
              );
            },
          },
        ],
      });
    },
    [machine.machineID]
  );

  /*
    Only used during BP when queue-mode is in repeat-one, and does not create a new mstarget session-event when sent.    

    We need to resend the last MS so FW knows whether to jitter or not between pitches, even if the ms is the same.
    Server takes care of providing the most up-to-date jitter flag in handleMsTarget.
  */
  const resendLastTarget = useCallback(
    (source: string) => {
      runSendTargetChecks({
        skipFiringCheck: true,
      });

      WebSocketService.send(WsMsgType.U2S_ResendMsTarget, lastMS, source);

      NotifyHelper.debug({
        message_md: `Resending S2M - ${lastMS.matching_hash} from ${source}!`,
      });
    },
    [lastMS]
  );

  // updates machine with latest auto-fire and last MS value
  // e.g. toggle by user or automatically disabled on unmount of certain components
  useEffect(() => {
    if (!isActive) {
      return;
    }

    const msg: IAutoFireMsg = {
      autoFire: autoFire,
    };

    WebSocketService.send(
      WsMsgType.U2M_AutoFire,
      msg,
      `auto-fire toggled ${autoFire ? 'ON' : 'OFF'}`
    );
  }, [autoFire]);

  const specialMsTarget = useCallback(
    (position: SpecialMsPosition) => {
      if (!isActive) {
        return false;
      }

      // safety
      setAutoFire(false);

      const sendMsg = () => {
        const data: ISpecialMstargetMsg = {
          position: position,
        };

        WebSocketService.send(
          WsMsgType.U2M_SpecialMsTarget,
          data,
          CONTEXT_NAME
        );

        setLastMSHash(undefined);
        setLastMS(DEFAULT_LAST_MS);
      };

      if (latestIsFiring.current) {
        lowerMachineRef.current = sendMsg;
        NotifyHelper.info({
          message_md: t('common.machine-will-lower-after-delay'),
        });
      } else {
        // timeout should allow the auto-resend of the last mstarget
        // from any changes to _autoFire to go through before the
        // special mstarget is sent
        setTimeout(sendMsg, 100);
      }

      return true;
    },
    [isActive]
  );

  useEffect(() => {
    const callback = (event: CustomEvent) => {
      const data: IMachineResetMsg = event.detail;

      specialMsTarget(SpecialMsPosition.home);

      NotifyHelper.warning({
        message_md: `Machine has been reset due to inactivity for ${data.idle_time} minutes.`,
      });
    };

    WebSocketHelper.on(WsMsgType.M2U_MachineReset, callback);
    return () => WebSocketHelper.remove(WsMsgType.M2U_MachineReset, callback);
  }, [specialMsTarget]);

  const [ballStatus, setBallStatus] = useState<IBallStatusMsg>();

  useEffect(() => {
    if (!ballStatus) {
      return;
    }

    setBallCount(ballStatus.ball_count);
    setBallDate(new Date());

    if (ballStatus.type !== BallStatusMsgType.AfterDropball) {
      return;
    }

    if (!useDropBallStore.getState().notifyResult) {
      return;
    }

    if (ballStatus.ball_count !== 1) {
      NotifyHelper.warning({
        message_md: `Drop ball failed.`,
        hideHeader: true,
      });
      return;
    }

    if (ballStatus.already_present) {
      NotifyHelper.success({ message_md: 'Ball is already present.' });
      return;
    }

    // fallback
    NotifyHelper.success({ message_md: 'Drop ball was successful!' });
  }, [ballStatus]);

  useEffect(() => {
    const callback = (event: CustomEvent) => {
      // ! release here, not in the effect above: setStateIfChanged dedups identical statuses
      useDropBallStore.getState().handleBallStatus(event.detail);

      ReactHelper.setStateIfChanged(setBallStatus, event.detail);
    };

    WebSocketHelper.on(WsMsgType.M2U_BallStatus, callback);
    return () => WebSocketHelper.remove(WsMsgType.M2U_BallStatus, callback);
  }, []);

  const [healthCheck, setHealthCheck] = useState<IHealthCheckResponseMsg>();

  useEffect(() => {
    if (!healthCheck) {
      return;
    }

    NotifyHelper.info({
      message_md: `Health check results are ready!`,
      delay_ms: 0,
      buttons: [
        {
          // only show button to re-open the dialog if it's not open already
          invisible: !healthCheckDialog,
          label: 'common.view-details',
          onClick: () => openDialog(MachineDialogMode.HealthCheck),
          dismissAfterClick: true,
        },
        {
          label: 'common.dismiss',
          onClick: () => {
            // do nothing
          },
          dismissAfterClick: true,
        },
      ],
    });
  }, [healthCheck]);

  // reset health check state if machine switched during health check
  useEffect(() => {
    setHealthCheck(undefined);
  }, [machine.machineID]);

  const healthChecking = healthCheckStatus === MachineStatus.HEALTH_CHECK;

  useEffect(() => {
    const callback = (event: CustomEvent) => {
      ReactHelper.setStateIfChanged(setHealthCheck, event.detail);
    };

    WebSocketHelper.on(WsMsgType.M2U_HealthCheck, callback);
    return () => WebSocketHelper.remove(WsMsgType.M2U_HealthCheck, callback);
  }, []);

  useEffect(() => {
    // if all ready, then assume there's 1 ball loaded
    if (r2fReady) {
      setBallCount(1);
      setBallDate(new Date());

      // r2f resolves any in-flight drop ball even if no ball status followed it
      useDropBallStore.getState().endDropBall();
      return;
    }

    if (noBall) {
      setBallCount(0);
      setBallDate(new Date());
    }
  }, [r2fReady, noBall]);

  const dropball = useCallback<DropBallFn>(
    (config) => {
      if (config.checkActive) {
        latestCheckActive.current(); // the result can be ignored since it's equal to isActive
      }

      if (!isActive) {
        return false;
      }

      if (specialMode === 'empty-carousel') {
        // its retry loop must not be throttled, but its preference still needs recording
        useDropBallStore.getState().setNotifyResult(!!config.notifyResult);
      } else {
        // for safety
        setAutoFire(false);

        if (!useDropBallStore.getState().beginDropBall(!!config.notifyResult)) {
          return false;
        }
      }

      MachineContextHelper.sendDropBall({
        machineID: machine.machineID,
        source: config.source,
      });

      return true;
    },
    [isActive, specialMode, machine.machineID]
  );

  // todo: update this logic so that we specify whether we are reconnecting user's WS or the machine is actually offline
  const reconnect = () => {
    if (!wsConnected) {
      // app WS is working, open dialog to troubleshoot machine
      openDialog(MachineDialogMode.Disconnected);
      return;
    }

    // app WS is not working, attempt reconnection first
    NotifyHelper.warning({
      message_md: t('common.attempting-to-reconnect-message'),
    });

    if (!current.token) {
      NotifyHelper.warning({
        message_md:
          'No session token found. Please login again to start a new session.',
      });
      return;
    }

    // attempt to reconnect
    createSocket(current.token);

    // open the dialog to monitor reconnection progress
    openDialog(MachineDialogMode.Disconnected);
  };

  const latestEStopPressed = useLatest(eStopPressed);
  const latestCalibrated = useLatest(calibrated);
  const latestCanCalibrate = useLatest(canCalibrate);

  // show toasts when queueState changes
  useEffect(() => {
    switch (queueState) {
      case QueueState.Busy: {
        NotifyHelper.warning({
          message_md: `You are waiting for \`${current.machineID}\`.`,
        });
        break;
      }

      case QueueState.Active: {
        // user became active, reset the hash just to be safe
        setLastMSHash(undefined);

        NotifyHelper.success({
          message_md: `You are now active on \`${current.machineID}\`.`,
        });

        if (latestEStopPressed.current === 'notify') {
          NotifyHelper.warning({
            message_md:
              'Please disengage the emergency stop before proceeding.',
            delay_ms: 0,
            buttons: [
              {
                label: 'common.dismiss',
                dismissAfterClick: true,
                onClick: () => {
                  // do nothing
                },
              },
            ],
          });
        }

        if (
          !latestCalibrated.current &&
          latestCanCalibrate.current === 'notify'
        ) {
          NotifyHelper.warning({
            message_md:
              'Please realign your machine before continuing your session.',
            delay_ms: 0,
            buttons: [
              {
                label: 'Realign Machine',
                dismissAfterClick: true,
                onClick: () => openDialog(MachineDialogMode.Realign),
              },
            ],
          });
        }
        break;
      }

      default: {
        break;
      }
    }
  }, [queueState]);

  // lower machine required while firing
  useEffect(() => {
    if (isFiring) {
      return;
    }

    if (!lowerMachineRef.current) {
      return;
    }

    // do it once firing is done
    lowerMachineRef.current();
    lowerMachineRef.current = null;
  }, [isFiring]);

  const [rapidMode, setRapidMode] = useState(false);

  // ball type should inform rapidMode
  useEffect(() => {
    // auto-enable depending on ball (no seams => enable rapidMode)
    setRapidMode(SEAMLESS_BALL_TYPES.includes(machine.ball_type));
    // force resend of any ms to ensure rapid mode is correctly set
    setLastMSHash(undefined);
  }, [machine.ball_type]);

  useEffect(() => {
    if (machine.last_installation?.end_date) {
      // force reload of DCs
      setLastDcLoaded(Date.now());
    }
  }, [machine.last_installation?.end_date]);

  const [vcc, setVcc] = useState<IVisionCameraCalibration>();

  // load or create VCC upon creation of VCC dialog
  useEffect(() => {
    if (!dialogs.VisionCamera) {
      setVcc(undefined);
      return;
    }

    const callback = async () => {
      const existing = machine.last_vccID
        ? await VCCService.getInstance().getOne(machine.last_vccID)
        : undefined;

      if (existing && !existing.complete) {
        // resume existing one
        setVcc(existing);
        return;
      }

      // create a new one
      const newVcc = await VCCService.getInstance().createOne({
        machineID: machine.machineID,
        capturedFrames: [],
      });

      if (!newVcc) {
        NotifyHelper.error({
          message_md: 'Failed to create Vision camera calibration object.',
        });
        return;
      }

      // attach it the machine
      update({ last_vccID: newVcc._id });

      // use the new vcc for dialog
      setVcc(newVcc);
    };

    callback();
  }, [dialogs.VisionCamera, machine.last_vccID, machine.machineID]);

  const openDialog = (
    value: MachineDialogMode,
    options?: IMachineDialogOptions
  ) => {
    if (value === MachineDialogMode.HwDiagnostic) {
      if (!options?.hwd) {
        console.error(
          'Cannot open HWD dialog without a HWD provided in options'
        );
        return;
      }

      useHardwareDiagnosticStore.getState().setInProgressHwd(options.hwd);
    }

    setDialogs((prev) => ({
      ...prev,
      [value]: {
        mode: value,
        key: Date.now(),
        options: options,
      },
    }));
  };

  const closeDialog = (dialog: MachineDialogMode) => {
    setDialogs((prev) => {
      const nextValue = { ...prev };
      nextValue[dialog] = undefined;
      return nextValue;
    });
  };

  const sendMultipleConnectionWarning = () => {
    NotifyHelper.warning({
      message_md: t('common.multiple-connections-warning'),
      delay_ms: 0,
    });
  };

  // if any wheel speed is non-zero, it is relevant to care about R2F
  const attemptingR2F = useMemo(
    // loading the screensaver should return false
    () => ![lastMS.w1, lastMS.w2, lastMS.w3].every((w) => w === 0),
    [lastMS]
  );

  const sendPitchPreview = useCallback<SendPitchPreviewFn>((config) => {
    const pitchToText = function (pitch: IPitch): string {
      const DISPLAY_DECIMALS = 0;
      const MIN_SPEED_LENGTH = '100'.length;

      const output: string[] = [];

      if (pitch.type && pitch.type !== PitchType.None) {
        output.push(pitch.type);
      }

      const speedMPH = TrajHelper.getSpeedFromTraj(pitch.traj);

      if (speedMPH !== undefined) {
        const value = machine.isMetric
          ? MetricHelper.mphToKph(speedMPH)
          : speedMPH;

        const units = MetricHelper.getSpeedUnit(machine.isMetric, true);

        output.push(
          `${value
            .toFixed(DISPLAY_DECIMALS)
            .padStart(MIN_SPEED_LENGTH)} ${units}`
        );
      }

      return output.join(' ');
    };

    const msg: IPitchPreviewOverlayMsg = {
      trigger: config.trigger,
      clear: false,
      lines: [pitchToText(config.current)],
      rich_lines: [],
    };

    if (config.prev) {
      msg.rich_lines.push({
        text: pitchToText(config.prev),
        color: 'grey',
        active: false,
      });
    }

    msg.rich_lines.push({
      text: pitchToText(config.current),
      color: 'white',
      active: true,
    });

    if (config.next) {
      msg.rich_lines.push({
        text: pitchToText(config.next),
        color: 'grey',
        active: false,
      });
    }

    WebSocketService.send(WsMsgType.S2M_PitchPreview, msg, CONTEXT_NAME);
  }, []);

  // Used in sendTarget and resendLastTarget before sending to machine
  const runSendTargetChecks = useCallback(
    (config: { skipFiringCheck: boolean }) => {
      if (!checkActive()) {
        console.debug('** runSendTargetChecks - not active');
        throw new Error('Not active user');
      }

      if (!config.skipFiringCheck && latestIsFiring.current) {
        console.debug('** runSendTargetChecks - not finished firing');
        throw new Error('Not finished firing');
      }

      /** validation stuff here */
      if (!calibrated) {
        console.debug('** runSendTargetChecks - not calibrated');
        NotifyHelper.warning({
          message_md:
            'Machine realignment required. Please realign machine first and try again.',
          buttons: [
            {
              label: 'Realign Machine',
              onClick: () => openDialog(MachineDialogMode.Realign),
            },
          ],
        });

        throw new Error('Machine is not calibrated');
      }

      // TODO: Add target/pitch/video validation from sendTarget?
      return true;
    },
    [checkActive, calibrated]
  );

  const sendTarget = useCallback<SendTargetFn>(
    async (config) => {
      try {
        // will throw an error if user is not active or machine is still firing
        runSendTargetChecks({
          skipFiringCheck: !!config.skipFiringCheck,
        });

        // if provided, ensure video would not crash the projector
        if (config.video) {
          if (config.video.MoundPixelY <= config.video.ReleasePixelY) {
            NotifyHelper.warning({
              message_md: `Video mound pixel Y (${config.video.MoundPixelY}) must be greater than release pixel Y (${config.video.ReleasePixelY}).`,
            });
            throw new Error('Invalid video mound pixel Y');
          }

          if (config.video.ReleaseHeight <= MOUND_HEIGHT_FT) {
            NotifyHelper.warning({
              message_md: `Video release height (${
                config.video.ReleaseHeight
              } ft) must be greater than mound height (${MOUND_HEIGHT_FT.toFixed(
                1
              )} ft).`,
            });
            throw new Error('Invalid video release height');
          }

          const CHECK_VIDEO_FRAME_AND_TIME = false;
          if (CHECK_VIDEO_FRAME_AND_TIME) {
            if (
              config.video.ReleaseFrame === undefined ||
              config.video.ReleaseFrame < 1 ||
              config.video.ReleaseFrame > config.video.n_frames
            ) {
              NotifyHelper.warning({
                message_md: `Video has an invalid release frame (${config.video.ReleaseFrame}).`,
              });
              throw new Error('Invalid video release frame');
            }

            if (
              config.video.ReleaseTime === undefined ||
              config.video.ReleaseTime < 0 ||
              config.video.ReleaseTime > config.video.ffmpeg.duration.seconds
            ) {
              NotifyHelper.warning({
                message_md: `Video has an invalid release time (${config.video.ReleaseTime}).`,
              });
              throw new Error('Invalid video release time');
            }
          }
        }

        const safeMsg: IMachineStateMsg = {
          ...config.msMsg,

          rapid:
            // e.g. auto-fire via settings
            config.msMsg.rapid ||
            // e.g. when enabled by a SA
            rapidMode,

          force: config.force ?? null,
        };

        if (!config.pitch._id) {
          // NotifyHelper.warning({ message_md: 'Sent pitch has no ID.', });
          config.pitch._id = `no-id-${v4()}`;

          console.warn({
            event: `${CONTEXT_NAME}: generated a temporary _id while sending a pitch without _id`,
            pitch: config.pitch,
          });
        }

        if (!config.pitch.bs) {
          NotifyHelper.error({
            message_md: 'Cannot send a pitch without a ball state.',
          });

          throw new Error('Cannot send a pitch without a ball state');
        }

        if (!config.pitch.traj) {
          NotifyHelper.error({
            message_md: 'Cannot send a pitch without a trajectory.',
          });

          throw new Error('Cannot send a pitch without a trajectory');
        }

        if (config.video) {
          safeMsg.video_uuid = config.video._id;

          const fatalVideoErrors = VideoHelper.getFatalErrors(config.video);
          if (fatalVideoErrors.length > 0) {
            NotifyHelper.error({
              message_md: [
                'Cannot send a pitch using a video with invalid metadata:',
                fatalVideoErrors
                  .filter((_, i) => i < 5)
                  .map((e) => ` - ${e.msg}`)
                  .join('\n'),
              ].join('\n\n'),
              delay_ms: 0,
            });

            throw new Error('Cannot send a pitch without valid metadata');
          }
        }

        // prefer per-pitch, fallback to per-list (if any)
        const pitcher = config.pitch.pitcher ?? config.list?.pitcher ?? '';
        // prefer per-pitch, fallback to per-list (if any)
        const customID = config.pitch.customID ?? config.list?.customID ?? null;

        const sessionData: IMSTargetEventData = {
          hw_config_id: machine.hw_config_id,
          pitch_id: config.pitch._id,
          bs_hash: config.pitch.bs_hash ?? 'none',

          list: config.list?.name ?? '',
          pitch_type: config.pitch.type ?? PitchType.None,
          pitch_title: config.pitch.name ?? '',
          pitcher: pitcher,
          pitcherCustomID: customID,
          video_title: config.video?.VideoTitle ?? '',
          build_priority: config.pitch.priority ?? BuildPriority.Spins,

          bs: config.pitch.bs,
          ms: safeMsg,
          traj: config.pitch.traj,

          plate: config.plate,
          ref_py: config.ref_py,

          breaks: config.pitch.breaks,

          hitterExt: config.hitter_id ? { _id: config.hitter_id } : undefined,

          collectionID: config.collectionID,

          aiming: config.aiming,
        };

        if (!config.force) {
          const errors: IBallDetailsError[] = MachineHelper.getSendTargetErrors(
            {
              bs: config.pitch.bs,
              traj: config.pitch.traj,
              ms: config.msMsg,
              releaseFt: machine.releaseFt,
            }
          );

          /** if one or more errors came up, log them all but don't proceed with sending to machine */
          if (errors.length > 0) {
            sessionData.errors = errors;

            const fixableErrors = errors.filter(
              (m) => m.fix?.autoFixMsFn !== undefined
            );

            const nonFixableErrors = errors.filter(
              (m) => m.fix?.autoFixMsFn === undefined
            );

            console.error({
              event: `${CONTEXT_NAME}: mstarget validation results`,
              fixable: fixableErrors.map((m) => m.msg),
              nonFixable: nonFixableErrors.map((m) => m.msg),
            });

            if (nonFixableErrors.length > 0) {
              NotifyHelper.error({
                message_md: `Cannot send pitch: ${nonFixableErrors[0].msg} (${
                  nonFixableErrors.length - 1
                } other errors)`,
              });

              throw new Error('Cannot send pitch with validation errors');
            }

            fixableErrors.forEach((m) => {
              if (m.fix?.autoFixMsFn) {
                config.msMsg = {
                  ...config.msMsg,
                  ...m.fix.autoFixMsFn(config.msMsg),
                };
              }
            });
          }
        }

        /** everything okay, send the ms */
        const response = await SessionEventsService.createOne({
          category: 'machine',
          tags: 'mstarget,sent',
          trigger: config.trigger,
          data: sessionData,
        });

        if (!response.success) {
          if (response.error === 'disconnected') {
            NotifyHelper.warning({
              message_md: t('refresh-due-to-network'),
            });
          }

          // todo: make this error handler more robust and based on an actual error code
          if (response.error?.includes('Prevented creation of')) {
            sendMultipleConnectionWarning();
          }

          throw new Error('Failed to create mstarget session event');
        }

        setLastPitchID(config.pitch._id);

        sendMsTarget(safeMsg, config.source);

        if (config.pitch._id) {
          useCookiesStore.getState().setCookie(CookieKey.snapshot, {
            pitch_id: config.pitch._id,
          });
        }

        return {
          success: true,
          // instead of assuming last MS hash updated in time (race condition)
          hash: MiscHelper.hashify(safeMsg),
        };
      } catch (e) {
        console.error(e);
        return {
          success: false,
        };
      }
    },
    [
      checkActive,
      rapidMode,
      machine.hw_config_id,
      calibrated,
      machine.releaseFt,
      runSendTargetChecks,
      sendMsTarget,
    ]
  );

  const setFireOption = useCallback(
    (flag: FireOption, enable: boolean) => {
      setFireOptions((prev) => {
        const nextValue = clone(prev);

        if (enable) {
          nextValue.add(flag);
        } else {
          nextValue.delete(flag);
        }

        return nextValue;
      });
    },
    [setFireOptions]
  );

  const resetMSHash = useCallback(
    () => setLastMSHash(undefined),
    [setLastMSHash]
  );

  const fetchMachine = useCallback(async () => {
    const result = await MachinesService.getInstance().getByMachineID(
      current.machineID
    );

    if (!result) {
      NotifyHelper.warning({
        message_md: 'Session expired, please login and try again.',
      });

      logout();
      return;
    }

    // reset hw warned flag whenever changing active machine
    if (machine.machineID !== result.machine.machineID) {
      setHardwareWarned(false);
    }

    safeSetMachine(result.machine);

    // todo: figure out why we do this from here
    useMachineStatusStore.setState({
      calibrated: result.calibrated,
    });
  }, [current.machineID, machine.machineID]);

  const logPlateDistanceChange = useCallback(
    (config: LogPlateConfig) => {
      const { prevPlateFt, nextPlateFt, overrideMachineID } = config;

      // spawn a service log entry
      MachineServiceLogsService.getInstance().createOne({
        machineID: overrideMachineID ?? machine.machineID,
        serviced: new Date().toISOString(),
        servicedBy: current.email,
        category: ServiceCategory.PlateDistanceChange,
        prevPlateFt: prevPlateFt,
        nextPlateFt: nextPlateFt,
        notes: isNil(prevPlateFt)
          ? // if there is no previous value
            `Plate distance was set to ${nextPlateFt.toFixed(2)}ft`
          : // if there is a previous value
            `Plate distance was updated from ${prevPlateFt.toFixed(
              2
            )}ft > ${nextPlateFt.toFixed(2)}ft`,
      });
    },
    [current.email, machine.machineID]
  );

  const unitFactors = useMemo(
    (): UnitFactors =>
      machine.isMetric
        ? {
            fromSpeedMph: MPH_TO_KPH,
            fromBreaksIn: IN_TO_CM,
            fromPosFt: FT_TO_INCHES * IN_TO_CM,
            fromDistanceFt: 1 / METERS_TO_FT,
          }
        : {
            fromSpeedMph: 1,
            fromBreaksIn: 1,
            fromPosFt: 1,
            fromDistanceFt: 1,
          },
    [machine.isMetric]
  );

  useSyncReadyToTrain({
    loading: loading,
    activeModel: activeModel,
    queueState: queueState,
    ignoreGameStatus: !!machine.ignore_game_status,
  });

  useSyncReadyToContTrain({
    loading: loading,
    activeModel: activeModel,
    queueState: queueState,
    trackingDevice: machine.tracking_device,
  });

  useSyncTmsDict(machine);

  useRecycleTmsShots(machine);

  const tmsDict = useTrainedMsStore((s) => s.tmsDict);

  const getTrainingPitches = useCallback(
    async (
      pitches: IPitch[],
      options?: {
        forceRebuild?: boolean;
      }
    ): Promise<ITrainingPitch[]> => {
      const pDict = ArrayHelper.firstBy(pitches, 'bs_hash');

      const sortedPitches: IPitch[] = [];

      for (const p of Object.values(pDict)) {
        if (!p) {
          continue;
        }

        sortedPitches.push(p);
      }

      sortedPitches.sort((a, b) => StringHelper.safeSort(a.name, b.name));

      // fill in TMS from tmsDict for any pitches that don't have a _mx_tms value
      for (const p of sortedPitches) {
        if (p._mx_tms) {
          continue;
        }

        const tms = tmsDict[p.bs_hash];

        if (!tms) {
          continue;
        }

        // in case we want to investigate why this pitch didn't automatically get a _mx_tms when tmsDict loaded/changed
        verboseDebug(
          props,
          'getTrainingPitches > pitch w/o _mx_tms where TMS entry exists',
          p,
          tms
        );

        p._mx_tms = tms;
      }

      // track original bs and ms (if any from _mx_tms)
      // replace with rebuilt values where necessary
      // key is pitch _id
      const outputDict: DictOf<{
        bs: IBallState; // updated to match build results but ignored for simplicity
        ms: IMachineState | undefined;
      }> = {};

      for (const pitch of sortedPitches) {
        if (!pitch._id) {
          pitch._id = `temp-${v4()}`;
        }

        outputDict[pitch._id] = {
          bs: pitch.bs,
          ms: pitch._mx_tms?.ms,
        };
      }

      // update _mx_ms for any pitches that need it
      const missingMs = sortedPitches.filter((p) => {
        if (!p) {
          return false;
        }

        if (options?.forceRebuild) {
          return true;
        }

        if (!p._mx_tms) {
          return true;
        }

        if (p._mx_tms.shotIDs.length === 0) {
          // no shots in TMS > could rebuild without losing progress
          if (p._mx_tms.skipRefresh) {
            // skip refreshing (e.g. cached result from a closed loop flow)
            return false;
          }

          // no shots > rebuild
          return true;
        }

        // pitch has an existing TMS (e.g. continue training)
        return false;
      }) as ITrainingPitch[];

      const canRebuild = missingMs.filter((p) => {
        if (!p) {
          return false;
        }

        switch (p.priority) {
          case BuildPriority.Breaks: {
            return !!activeModel?.supports_breaks;
          }

          case BuildPriority.Spins:
          case BuildPriority.Default:
          default: {
            return !!activeModel?.supports_spins;
          }
        }
      });

      if (canRebuild.length > 0) {
        if (canRebuild.length > 5) {
          // show toast if we have to rebuild a bunch of pitches, but show the original number in msg to avoid confusing the user
          NotifyHelper.info({
            message_md: `Please wait while we prepare ${pitches.length} pitches for training...`,
          });
        }

        verboseDebug(
          props,
          `rebuilding ${canRebuild.length} pitch(es)`,
          canRebuild
        );

        const buildResults =
          await StateTransformService.getInstance().buildMany({
            machine: machine,
            notifyError: true,
            chars: canRebuild.map((p) => {
              const o: IBuildPitchPayload = {
                build_id: p._id,
                priority: p.priority ?? BuildPriority.Default,
                bs: p.bs,
                bs_hash: p.bs_hash,
                traj: p.traj,
                plate: p.plate_loc_backup ?? DEFAULT_PLATE,
                seams: p.seams ?? DEFAULT_SEAMS,
                breaks: p.breaks,
              };

              return o;
            }),
          });

        if (buildResults) {
          for (const entry of Object.values(buildResults)) {
            if (!entry) {
              // shouldn't trigger
              continue;
            }

            if (!entry.chars) {
              console.error(
                `No chars on build result for build_id "${entry.build_id}"`
              );
              continue;
            }

            const outputEntry = outputDict[entry.build_id];

            if (!outputEntry) {
              continue;
            }

            // ? bs is perturbed but not by a lot, according to spot testing
            outputEntry.bs = entry.chars.bs;
            outputEntry.ms = entry.chars.ms;
          }
        }
      }

      const output = compact(
        sortedPitches.map((pitch) => {
          if (!pitch) {
            return undefined;
          }

          const entry = outputDict[pitch._id];

          if (!entry) {
            console.error(`Failed to find entry for ${pitch._id}`, outputDict);
            return undefined;
          }

          if (!entry.ms) {
            console.warn(`Failed to find ms on entry for ${pitch._id}`, entry);
            return undefined;
          }

          const o: ITrainingPitch = {
            ...pitch,
            // * ignore diff bs_hash implied by slightly diff bs
            _mx_ms: entry.ms,
          };

          return o;
        })
      );

      if (output.length === 0) {
        NotifyHelper.warning({
          message_md: 'There are no trainable pitches in your selection.',
        });
        return [];
      }

      return output;
    },
    [machine, activeModel, tmsDict]
  );

  const restartOS = useCallback(
    (source: string) => {
      if (!checkActive()) {
        console.warn(`${CONTEXT_NAME}: restart OS skipped (${source})`);
        return;
      }

      console.warn(`${CONTEXT_NAME}: restart OS triggered (${source})`);

      MachinesService.getInstance().restart(RestartMode.os, [
        machine.machineID,
      ]);
    },
    [checkActive, machine.machineID]
  );

  // MARK: state
  const state: IMachineContext = {
    lastFired: lastFired,
    lastFireID: lastFireID,

    rapidMode: rapidMode,
    setRapidMode: setRapidMode,

    checkConfidence: !fireOptions.has(FireOption.SkipConfidenceCheck),

    machine: machine,
    unitFactors: unitFactors,

    activeStrikeZone: activeStrikeZone,

    isActive: isActive,

    activeModel: activeModel,

    machineModels: models,

    lastPitchID: lastPitchID,

    lastMS: lastMS,

    lastMSHash: lastMSHash,
    resetMSHash: resetMSHash,

    dialogs: dialogs,
    openDialog,
    closeDialog,

    lastHwdCancelled: lastHwdCancelled,
    onCancelHwd: () => setLastHwdCancelled(Date.now()),

    attemptingR2F: attemptingR2F,

    lastBallCount: ballCount,

    lastOverlayIDs: lastOverlayIDs,

    fireOptions: fireOptions,
    setFireOption: setFireOption,

    calibrating: calibrating,

    autoFire: autoFire,
    setAutoFire: setAutoFire,

    loading: loading,

    process_statuses: processStatuses,
    setProcessData: (procQueryResponse: IProcessQueryResponseMsg) => {
      const processStatus = procQueryResponse.status;
      if (processStatus === true) {
        setProcessStatuses(procQueryResponse.processes);
      }
    },

    sendControlResponse: (msg) => {
      if (!checkActive()) {
        return;
      }

      WebSocketService.send(
        WsMsgType.Misc_ControlResponse,
        msg,
        'machine context'
      );
      NotifyHelper.info({
        message_md: `Response sent to \`${msg.waitingWsID}\`.`,
      });
    },

    checkActive: checkActive,

    specialMsTarget: specialMsTarget,

    dropball: dropball,

    update: update,

    recentlyFiredPitch: recentFiredPitch,

    fire: async (config) => {
      try {
        if (!checkActive()) {
          verboseDebug(props, 'fire > failed > inactive user');
          return { success: false };
        }

        if (
          lastFired !== undefined &&
          Date.now() - lastFired < MIN_WAIT_BETWEEN_FIRE_MS
        ) {
          verboseDebug(props, 'fire > failed > recent last fire');
          return { success: false };
        }

        const { fireData } = config;

        console.log(`${CONTEXT_NAME}: firing pitch`, fireData);

        const payload: Partial<IFireEvent> = {
          category: 'machine',
          tags: 'fire',
          data: {
            mode: fireData.mode,

            ball_type: machine.ball_type,
            pitch_id: config.pitch._id,
            pitch_name: config.pitch.name,
            pitch_type: config.pitch.type ?? PitchType.None,

            training: fireData.training,
            training_mode: fireData.training_mode,

            onDemand: onDemand,

            in_game: isInGame,

            trigger: fireData.trigger,
            options: safeFireOptions,
          },
        };

        const hitter = hitters.find((h) => h._id === fireData.hitter_id);

        if (payload.data) {
          payload.data.strikezone = PlateHelper.getDynamicStrikeZone(
            machine.strikeZoneType,
            hitter?.height_ft
          );

          if (hitter) {
            /** update hitter stats */
            const stats = await increasePitchCount(hitter._id);

            /** assemble and attach extended hitter object to fire event  */
            payload.data.hitterExt = {
              _id: fireData.hitter_id,
              // if details change, this would trigger server to reload from DB before sending to machine
              _changed: hitters.find((h) => h._id === fireData.hitter_id)
                ?._changed,
              stats: stats,
            };
          }

          if (fireData.tags) {
            const tags = ArrayHelper.unique(
              fireData.tags
                .toUpperCase()
                .split(',')
                .map((t) => t.trim())
            );

            if (tags.length > 0) {
              payload.data.tags = tags;
            }
          }
        }

        // create the fire event
        const response = await SessionEventsService.createOne(payload);
        if (!response.success) {
          if (response.error === 'disconnected') {
            NotifyHelper.warning({
              message_md:
                'There was a problem processing your request. Your connection has been reset. Please try again or contact support.',
            });
            reconnect();
          }

          // todo: make this error handler more robust and based on an actual error code
          if (response.error?.includes('Prevented creation of')) {
            sendMultipleConnectionWarning();
          }

          return { success: false };
        }

        const fireEvent = response.data as IFireEvent | undefined;

        if (!fireEvent) {
          if (response.error) {
            NotifyHelper.warning({
              message_md: `There was a problem logging the fire event (${response.error}). Please try again or contact support.`,
            });
            return { success: false };
          }

          // session event creation was rejected (likely for auth-reasons)
          NotifyHelper.warning({
            message_md: `Your session has expired. Please logout and login before trying again.`,
          });
          return { success: false };
        }

        const data: IFireMsg = {
          fire_id: fireEvent._id,
          strikeZone: fireData.training ? VISIBLE_STRIKEZONE : activeStrikeZone,
          hitterSide: !fireData.training && hitter ? hitter.side : null,
        };

        await WebSocketService.send(WsMsgType.U2S_Fire, data, fireData.trigger);

        /** track fired shots for session */
        increaseFired();

        /** take note that the pitch was recently fired */
        setRecentFiredPitch(config.pitch);
        setLastFired(Date.now());
        setLastFireID(fireEvent._id);

        return {
          event: fireEvent,
          success: true,
        };
      } catch (e) {
        console.error(e);

        return {
          event: undefined,
          success: false,
        };
      }
    },

    send2FA: (source, msg) => {
      WebSocketService.send(WsMsgType.S2M_TwoFa, msg, source);
    },

    requestOverlayIDs: (source) => {
      if (!isActive) {
        return;
      }

      WebSocketService.send(WsMsgType.U2S_Overlays, {}, source);
    },

    setOverlayIDs: (values) => {
      setLastOverlayIDs(values);
    },

    sendProcessReset: (source, proc) => {
      if (!checkActive()) {
        return;
      }

      WebSocketService.send(WsMsgType.Process_Reset, proc, source);
    },

    sendProcessQuery: (source) => {
      if (!checkActive()) {
        return;
      }

      NotifyHelper.success({
        message_md: `Querying \`${machine.machineID}\` for running processes...`,
      });

      WebSocketService.send(WsMsgType.Process_Query, {}, source);
    },

    sendProcessKill: (source, proc) => {
      if (!checkActive()) {
        return;
      }

      WebSocketService.send(WsMsgType.Process_Kill, proc, source);
    },

    restartOS: restartOS,

    restartFW: (source) => {
      if (!checkActive()) {
        console.warn(`${CONTEXT_NAME}: restart FW skipped (${source})`);
        return;
      }

      console.warn(`${CONTEXT_NAME}: restart FW triggered (${source})`);

      MachinesService.getInstance().restart(RestartMode.arc, [
        machine.machineID,
      ]);
    },

    calibrate: (procs, source) => {
      if (!checkActive()) {
        return;
      }

      const message: ICalibrateRequestMsg = {
        procs: procs,
      };

      const hasNonProjectorProcs =
        procs.filter(
          (p) =>
            ![
              CalibrateProc.ProjectorOutline,
              CalibrateProc.ProjectorRulers,
            ].includes(p)
        ).length > 0;

      // only wait for calibration completion if there are non-projector procs being calibrated
      if (hasNonProjectorProcs) {
        // ! this should go away once projector calibration uses its own pathways
        setCalibrating(true);
        WebSocketService.send(WsMsgType.U2S_Calibrate, message, source);
        return;
      }

      WebSocketService.send(WsMsgType.U2S_Calibrate, message, source);

      // NotifyHelper.info({ message_md: `Calibration started, this will take approximately 1 minute. Please wait...` });
    },

    sendRawTarget: async (msg, source, silently) => {
      try {
        if (!silently) {
          checkActive();
        }

        if (!isActive) {
          return false;
        }

        if (latestIsFiring.current) {
          if (!silently) {
            NotifyHelper.warning({
              message_md: `Please wait for ${machine.machineID} to finish firing before trying again.`,
            });
          }
          return false;
        }

        if (!silently) {
          NotifyHelper.info({
            message_md: 'Sending raw mstarget to machine...',
          });
        }

        const data: IMSTargetEventData = {
          pitch_id: 'raw',
          bs_hash: 'none',

          list: '',
          pitch_type: PitchType.None,
          pitch_title: '',
          pitcher: '',
          pitcherCustomID: null,
          video_title: '',
          build_priority: BuildPriority.Default,

          bs: DEFAULT_BALL_STATE,
          ms: msg,
          traj: DEFAULT_TRAJECTORY,

          ref_py: DEFAULT_BALL_STATE.py,
        };

        const response = await SessionEventsService.createOne({
          category: 'machine',
          tags: 'mstarget,sent',
          trigger: source,
          data: data,
        });

        if (!response.success) {
          // todo: make this error handler more robust and based on an actual error code
          if (response.error?.includes('Prevented creation of')) {
            sendMultipleConnectionWarning();
          }
          throw new Error('Failed to create mstarget event for raw target.');
        }

        sendMsTarget(msg, source);
        return true;
      } catch (e) {
        console.error(e);
        return false;
      }
    },

    sendPitchPreview: sendPitchPreview,

    sendTarget: sendTarget,
    resendLastTarget: resendLastTarget,

    toggleRuler(source, msg) {
      if (!checkActive()) {
        return;
      }

      NotifyHelper.success({
        message_md: `Toggling ruler for \`${machine.machineID}\``,
      });

      WebSocketService.send(WsMsgType.U2S_Ruler, msg, source);
    },

    adjustKeystone(source, msg) {
      if (!checkActive()) {
        return;
      }

      specialMsTarget(SpecialMsPosition.home);

      WebSocketService.send(WsMsgType.U2S_Keystone, msg, source);
    },

    specialMode: specialMode,
    setSpecialMode: setSpecialMode,

    onEndTraining: () => {
      if (machine.enable_auto_reset_ms) {
        specialMsTarget(SpecialMsPosition.lowered);
        setLastMSHash(undefined);
        return;
      }

      // only change the video + stop the wheels, leave the other aspects of the machine intact

      const msg: IMachineStateMsg = {
        ...lastMS,

        w1: 0,
        w2: 0,
        w3: 0,

        video_uuid: StaticVideoType.screensaver,
      };

      sendMsTarget(msg, 'training ended');
      setLastMSHash(undefined);

      // TODO: Only ask this if the machine is not already lowered
      if (!lowerMachineRef.current) {
        /** show toast asking user if they want to bring down the machine if it's not already lowering */
        NotifyHelper.warning({
          message_md: [
            t('tr.complete-msg'),
            t('common.lower-machine-question'),
          ].join('\n\n'),
          delay_ms: 10 * 1000,
          buttons: [
            {
              label: 'common.lower-machine',
              dismissAfterClick: true,
              onClick: () => specialMsTarget(SpecialMsPosition.lowered),
            },
          ],
        });
      }
    },

    playSound: (effect) => {
      if (!isActive) {
        return;
      }

      const data: IProjectorSfxMsg = {
        name: effect,
      };

      WebSocketService.send(
        WsMsgType.Misc_ProjectorSoundFx,
        data,
        CONTEXT_NAME
      );
    },

    getDefaultPitch: (priority) => {
      const bs: IBallState = clone(DEFAULT_BALL_STATE);
      // todo: do we need to force py to be 56.5 instead
      bs.py = machine.releaseFt;

      const o: IPitch = {
        _id: '',
        _created: '',
        _changed: '',
        _parent_id: '',
        _parent_def: 'pitch-lists',
        _parent_field: 'pitches',
        bs: bs,
        bs_hash: MachineHelper.getBsHash(bs, priority),
        traj: {
          ...DEFAULT_TRAJECTORY,
          // todo: do we need to force py to be 56.5 instead
          py: machine.releaseFt,
        },
        plate_loc_backup: DEFAULT_PLATE,
        breaks: {
          zInches: 0,
          xInches: 0,
        },
        seams: BallHelper.getSeamsFromBS(bs),
        priority: priority || app.build_priority,
        windupVideoID: null,
        stretchVideoID: null,
      };

      return o;
    },

    healthChecking: healthChecking,
    healthCheckResults: healthCheck,
    startHealthCheck: (type) => {
      if (healthChecking) {
        // prevent re-start while a check is running
        NotifyHelper.warning({
          message_md:
            'Please wait for the current health check to complete first.',
        });
        return;
      }

      setHealthCheck(undefined);

      const msg: IHealthCheckRequestMsg = {
        type: type,
      };

      WebSocketService.send(WsMsgType.U2M_HealthCheck, msg, 'admin request');
    },

    reconnect: reconnect,

    lastDcComplete: lastDcComplete,
    reloadLastDcComplete: () => setLastDcLoaded(Date.now()),

    createDataCollection: createDataCollection,

    extendStandaloneInstallation: async () => {
      const { last_installation, machineID, enable_trajekt_vision } = machine;

      if (!last_installation?.standalone) {
        // do nothing
        return;
      }

      const nextInstall = clone(last_installation);

      if (nextInstall.standalone) {
        // clear the standalone-ness
        nextInstall.standalone = null;
      }

      // * create any necessary records that might be missing

      if (!nextInstall.btcID || !nextInstall.rtID) {
        const collections = await ensureInstallationDataCollections({
          machineID,
          ballType: nextInstall.ball_type,
          installation: nextInstall,
          createDataCollection,
        });

        if (!nextInstall.btcID) {
          nextInstall.btcID = collections.btcID ?? '';
        }

        if (!nextInstall.rtID) {
          nextInstall.rtID = collections.rtID ?? '';
        }
      }

      if (enable_trajekt_vision && !nextInstall.vccID) {
        const vcc = await VCCService.getInstance().createOne({
          machineID: machineID,
          capturedFrames: [],
        });

        nextInstall.vccID = vcc?._id ?? null;
      }

      await update({ last_installation: nextInstall }, true);
    },

    fetchMachine: fetchMachine,

    logPlateDistanceChange: logPlateDistanceChange,

    getTrainingPitches: getTrainingPitches,
  };

  /** refetch the data whenever machineID changes */
  useEffect(() => {
    if (!current.machineID) {
      setMachine(DEFAULT.machine);
      return;
    }

    fetchMachine();
  }, [current.machineID]);

  useEffect(() => {
    if (recentFiredPitch && recentFiredPitch._id) {
      setTimeout(() => {
        /** clear recently fired after a delay */
        setRecentFiredPitch(undefined);
      }, RECENTLY_FIRED_DURATION);
    }
  }, [recentFiredPitch]);

  useEffect(() => {
    if (!current.auth || !machine.machineID) {
      setModels([]);
      return;
    }

    // if the machine models haven't changed, no need to re-fetch
    if (isEqual(machine.model_ids, prevMachineModelIDs)) {
      return;
    }

    MachinesService.getInstance()
      .getMachineModels(machine.machineID)
      .then((result) => setModels(result));
  }, [
    current.auth,
    // when changing machines, reload the relevant models
    machine.machineID,
    // when changing which models are used by this machine, reload relevant models
    machine.model_ids,
  ]);

  // whenever ball count is updated (even if the number doesn't change)
  // run the logic to show / hide the inspect option and the notification toast
  useEffect(() => {
    if (ballCount === undefined) {
      // we have no idea what's what yet
      return;
    }

    if (ballCount === 1) {
      // nothing is wrong
      return;
    }

    if (ballStatusToast) {
      // toast is already visible
      return;
    }

    switch (specialMode) {
      case 'empty-carousel':
      case 'inspect-machine':
        return;

      default: {
        const buttons: INotificationButton[] = [
          {
            label: t('main.inspect'),
            onClick: () => openDialog(MachineDialogMode.Inspect),
            dismissAfterClick: true,
          },
          {
            label: t('common.lower-machine'),
            onClick: () => specialMsTarget(SpecialMsPosition.home),
            dismissAfterClick: true,
          },
        ];

        switch (ballCount) {
          case -1: {
            if (SUPPRESS_LOW_LIGHT_WARNING) {
              return;
            }

            setBallStatusToast(true);
            NotifyHelper.warning({
              delay_ms: 0,
              header: 'common.lighting-error',
              message_md: t('common.lighting-error-msg'),
              buttons: buttons,
              onClose: () => setBallStatusToast(false),
            });
            break;
          }

          case 0: {
            // no balls detected
            buttons.splice(0, 0, {
              label: t('main.drop-ball'),
              onClick: () =>
                dropball({
                  source: 'ball-status toast',
                  checkActive: true,
                  notifyResult: true,
                }),
              dismissAfterClick: true,
            });

            setBallStatusToast(true);
            NotifyHelper.warning({
              delay_ms: 0,
              header: 'common.no-ball-detected',
              message_md: t('common.no-ball-detected-msg'),
              buttons: buttons,
              onClose: () => setBallStatusToast(false),
            });
            break;
          }

          default: {
            if (ballCount > 1) {
              // multiple balls detected
              setBallStatusToast(true);
              NotifyHelper.warning({
                delay_ms: 0,
                header: 'common.multiple-balls-detected',
                message_md: t('common.multiple-balls-detected-msg'),
                buttons: buttons,
                onClose: () => setBallStatusToast(false),
              });
            }
            break;
          }
        }

        return;
      }
    }
  }, [ballDate]);

  // MARK: dialog defs
  const installationDialog = dialogs[MachineDialogMode.Installation];
  const healthCheckDialog = dialogs[MachineDialogMode.HealthCheck];
  const connectionDialog = dialogs[MachineDialogMode.Disconnected];
  const hwDiagnosticDialog = dialogs[MachineDialogMode.HwDiagnostic];
  const visionCameraDialog = dialogs[MachineDialogMode.VisionCamera];
  const visionDistanceDialog = dialogs[MachineDialogMode.VisionDistance];
  const requestControlDialog = dialogs[MachineDialogMode.RequestControl];
  const realignDialog = dialogs[MachineDialogMode.Realign];
  const inspectDialog = dialogs[MachineDialogMode.Inspect];
  const statusDialog = dialogs[MachineDialogMode.Status];
  const restartDialog = dialogs[MachineDialogMode.Restart];
  const debugDialog = dialogs[MachineDialogMode.Debug];

  // MARK: render
  return (
    <MachineContext.Provider value={state}>
      <EndTrainingListener />
      <IntercomProvider
        appId={env.integrations.intercom_app_id}
        autoBootProps={{
          email: current.email,
          userId: current.userID,
          customAttributes: {
            'Session ID': current.session,
            'Team ID': current.teamID,
            Team: current.team,
            League: current.league,
            Machine: current.machineID,
            'Machine Nickname': current.machineNickname ?? '(none)',
          },
        }}
        autoBoot
      >
        <InstallationProvider
          installation={machine.last_installation}
          onCancel={() => {
            setAutoFire(false);
            specialMsTarget(SpecialMsPosition.lowered);
            closeDialog(MachineDialogMode.Installation);
            setLastDcLoaded(Date.now());
          }}
        >
          <IntercomListener />
          {props.children}

          {healthCheckDialog && (
            <HealthCheckDialog
              key={healthCheckDialog.key}
              onClose={() => closeDialog(MachineDialogMode.HealthCheck)}
            />
          )}

          {hwDiagnosticDialog && hwDiagnosticDialog.options?.hwd && (
            <HardwareDiagnosticDialog
              key={hwDiagnosticDialog.key}
              hwd={hwDiagnosticDialog.options.hwd}
              onClose={() => {
                setAutoFire(false);
                specialMsTarget(SpecialMsPosition.lowered);
                closeDialog(MachineDialogMode.HwDiagnostic);
                setLastDcLoaded(Date.now());
                useHardwareDiagnosticStore
                  .getState()
                  .loadInProgressHwd(machine.machineID);
              }}
            />
          )}

          <HardwareDiagnosticCancelConfirm />

          {visionCameraDialog && vcc && (
            <CommonDialog
              key={visionCameraDialog.key}
              identifier="MachineVisionCameraDialog"
              title={
                <Heading size="5">
                  Stereo Camera Calibration <Badge>{machine.machineID}</Badge>
                </Heading>
              }
              width={RADIX.DIALOG.WIDTH.LG}
              content={
                <VisionCalibrationCameraHoC
                  identifier="MachineVCC"
                  vccID={vcc._id}
                  onComplete={() => {
                    // next time, start over
                    update({ last_vccID: null });

                    closeDialog(MachineDialogMode.VisionCamera);

                    // make sure the user goes to the distance calibration step next
                    openDialog(MachineDialogMode.VisionDistance);
                  }}
                  verbose
                />
              }
              loading={loading}
              onClose={() => closeDialog(MachineDialogMode.VisionCamera)}
            />
          )}

          {visionDistanceDialog && (
            <CommonDialog
              key={visionDistanceDialog.key}
              identifier="MachineVisionDistanceDialog"
              title={
                <Heading size="5">
                  Distance to Plate Calibration{' '}
                  <Badge>{machine.machineID}</Badge>
                </Heading>
              }
              width={RADIX.DIALOG.WIDTH.LG}
              content={
                <VisionCalibrationDistanceHoC
                  identifier="MachineVCD"
                  onUpdate={(position) => {
                    // * reminder: plate distance + inset = release distance
                    console.debug(
                      'Received calibrated position from Vision',
                      position
                    );
                  }}
                  onComplete={() =>
                    closeDialog(MachineDialogMode.VisionDistance)
                  }
                  onCancel={() => closeDialog(MachineDialogMode.VisionDistance)}
                  verbose
                />
              }
              loading={loading}
              onClose={() => closeDialog(MachineDialogMode.VisionDistance)}
            />
          )}

          <NotificationListener
            onRestartOS={() => {
              if (!checkActive()) {
                console.warn(`${CONTEXT_NAME}: restart OS skipped (toast)`);
                return;
              }

              console.warn(`${CONTEXT_NAME}: restart OS triggered (toast)`);

              MachinesService.getInstance().restart(RestartMode.os, [
                machine.machineID,
              ]);
            }}
          />

          {installationDialog && (
            <InstallationDialogHoC
              key={installationDialog.key}
              identifier="MachineInstallationDialog"
              onClose={() => {
                setAutoFire(false);
                specialMsTarget(SpecialMsPosition.lowered);
                closeDialog(MachineDialogMode.Installation);
                setLastDcLoaded(Date.now());
              }}
            />
          )}

          {requestControlDialog && (
            <MachineControlDialog
              key={requestControlDialog.key}
              identifier="MachineControlDialog"
              onClose={() => closeDialog(MachineDialogMode.RequestControl)}
            />
          )}

          {realignDialog && (
            <RealignMachineDialog
              key={realignDialog.key}
              onClose={() => closeDialog(MachineDialogMode.Realign)}
            />
          )}

          {statusDialog && (
            <R2fStatusDialog
              key={statusDialog.key}
              identifier="R2FStatusDialog"
              onClose={() => closeDialog(MachineDialogMode.Status)}
            />
          )}

          {inspectDialog && (
            <MachineInspectionDialogHoC
              key={inspectDialog.key}
              mode="self"
              machineID={machine.machineID}
              hwVersion={machine.hardware_version}
              identifier="MachineInspectionDialog"
              onClose={() => closeDialog(MachineDialogMode.Inspect)}
            />
          )}

          {connectionDialog && (
            <MachineConnectionDialog
              key={connectionDialog.key}
              identifier="MachineConnectionDialog"
              onClose={() => closeDialog(MachineDialogMode.Disconnected)}
            />
          )}

          {restartDialog && (
            <CommonConfirmationDialog
              key={restartDialog.key}
              identifier="MachineConfirmRestart"
              title="common.restart"
              content={<Box>{t('common.machine-not-in-use-msg')}</Box>}
              action={{
                onClick: () => restartOS('MachineConfirmRestart'),
              }}
            />
          )}

          {debugDialog && (
            <MSDebugDialogHoC
              key={debugDialog.key}
              identifier="MachineDebugDialog"
              onClose={() => closeDialog(MachineDialogMode.Debug)}
            />
          )}
        </InstallationProvider>
      </IntercomProvider>
    </MachineContext.Provider>
  );
};
